// Schema de un paro embebido dentro de un registro_produccion_hora.
//
// La BD enforce:
//   - causal_madre_id + causal_id del mismo tenant (mig 00013)
//   - causal_id es hija de causal_madre_id (o ambas iguales si es paro a nivel
//     madre directamente — caso raro)
//
// Acá enforce a nivel Zod:
//   - hora_fin > hora_inicio (o null si está abierto)
//   - observación obligatoria (≥5 chars) si la causal lo requiere ("Otro")
import { z } from 'zod';
/** Payload mínimo para crear un paro asociado a un registro nuevo. */
export const paroCreateSchema = z
    .object({
    causal_madre_id: z.string().uuid(),
    causal_id: z.string().uuid(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }).nullable(),
    observaciones: z.string().trim().max(500).nullable(),
    /**
     * Indica si la causal seleccionada requiere observación (cliente lo sabe
     * porque vino en el tree de /api/causales-paro). El backend re-valida.
     */
    requiere_observacion: z.boolean().default(false),
})
    .refine((p) => p.hora_fin === null || p.hora_fin > p.hora_inicio, {
    message: 'hora_fin debe ser posterior a hora_inicio',
    path: ['hora_fin'],
})
    .refine((p) => !p.requiere_observacion || (p.observaciones !== null && p.observaciones.length >= 5), {
    message: 'Observación obligatoria (mínimo 5 caracteres) para esta causal',
    path: ['observaciones'],
});
/** Schema completo de un paro persistido (lo que devuelve el backend). */
export const paroSchema = z.object({
    id: z.string().uuid(),
    paro_evento_id: z.string().uuid().optional(),
    tenant_id: z.string().uuid(),
    registro_produccion_hora_id: z.string().uuid(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }).nullable(),
    minutos_perdidos: z.number().nullable(),
    causal_madre_id: z.string().uuid(),
    causal_id: z.string().uuid(),
    observaciones: z.string().nullable(),
    linea_id: z.string().uuid(),
    cerrado_por_supervisor: z.boolean(),
    razon_cierre_supervisor: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
    // Ingesta de máquina (propuesta 2026-10-alimentos-rionegro). Opcionales: los
    // endpoints que no los proyectan siguen parseando igual (ausencia ⇒ undefined).
    pendiente_causal: z.boolean().optional(),
    ref_externa: z.string().nullable().optional(),
});
/**
 * Payload del PATCH /api/paros/:id/cerrar — el líder cierra un paro que dejó
 * abierto en un bloque anterior del turno. Setea hora_fin + observaciones
 * opcional. Si al líder se le pasa el turno, el cierre lo hace el TÉCNICO de
 * mantenimiento (`PATCH /api/mantenimiento/paros/:id/cerrar`); el supervisor NO
 * cierra paros — los confirma o los corrige.
 *
 * ⚠️ `cerrado_por_supervisor` / `razon_cierre_supervisor` (mig 006) son columnas
 * que NINGÚN endpoint escribe: el flujo que las justificaba quedó descartado.
 * Siempre son FALSE/NULL — no usarlas como señal.
 */
export const paroCerrarSchema = z.object({
    hora_fin: z.string().datetime({ offset: true }),
    observaciones: z.string().trim().max(500).nullable().default(null),
});
/**
 * Vista enriquecida que devuelve GET /api/paros/abiertos — paros sin hora_fin
 * del turno actual del líder, con contexto del bloque (línea + bloque) y de la
 * causal (nombres madre + específica + flag requiere_observacion) para que el
 * banner pueda renderizar sin queries adicionales.
 */
export const paroAbiertoSchema = z.object({
    id: z.string().uuid(),
    registro_produccion_hora_id: z.string().uuid(),
    hora_inicio: z.string().datetime({ offset: true }),
    causal_madre_id: z.string().uuid(),
    causal_madre_nombre: z.string(),
    causal_id: z.string().uuid(),
    causal_nombre: z.string(),
    requiere_observacion: z.boolean(),
    observaciones: z.string().nullable(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    // Contexto del bloque al que pertenece el paro (donde se originó)
    bloque_fecha: z.string().date(),
    bloque_hora_inicio: z.string(),
    bloque_hora_fin: z.string(),
    turno_id: z.string().uuid(),
    // Ingesta de máquina: paro abierto por la máquina sin causal (la causal es la
    // "Sin clasificar (máquina)" del tenant hasta que una persona la completa).
    pendiente_causal: z.boolean().optional(),
    ref_externa: z.string().nullable().optional(),
});
export const parosAbiertosListSchema = z.array(paroAbiertoSchema);
/**
 * Vista que devuelve GET /api/paros/cerrados-por-mtto — paros del líder que
 * MANTENIMIENTO cerró por él durante el turno vigente.
 *
 * Por qué existe: cuando el técnico cierra un paro que el líder dejó abierto,
 * el paro simplemente DESAPARECE del banner de `/abiertos` y el líder nunca se
 * entera de con qué hora quedó. Este endpoint alimenta ese aviso (decisión del
 * responsable, 2026-08-14).
 *
 * Por qué no usa la campana de notificaciones: `GET /notificaciones` está
 * gateado por `verMantenimiento` y la campana vive dentro de
 * `MantenimientoShell` — un líder no tiene esa capability. El aviso va inline
 * en L2, con el mismo patrón del banner de paros abiertos.
 */
export const paroCerradoPorMttoSchema = z.object({
    id: z.string().uuid(),
    registro_produccion_hora_id: z.string().uuid(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }),
    minutos_perdidos: z.number().nullable(),
    causal_madre_nombre: z.string(),
    causal_nombre: z.string(),
    observaciones: z.string().nullable(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    bloque_hora_inicio: z.string(),
    bloque_hora_fin: z.string(),
    /** Quién lo cerró (el técnico de mantenimiento). */
    cerrado_por: z.object({
        id: z.string().uuid(),
        nombre: z.string(),
        apellido: z.string(),
    }),
});
export const parosCerradosPorMttoListSchema = z.array(paroCerradoPorMttoSchema);
//# sourceMappingURL=paro.js.map